import { useIonModal } from '@ionic/react';
import { MutationDefinition } from '@reduxjs/toolkit/dist/query';
import { UseMutation } from '@reduxjs/toolkit/dist/query/react/buildHooks';
import { Document } from 'model';
import { ReactElement } from 'react-markdown/lib/react-markdown';
import { Confirmation } from '../pages/Confirmation';
// eslint-disable-next-line css-modules/no-unused-class
import { BaseQueryType } from '../api/baseQueryWithReauth.js';
import { IconButton } from './IconButton';
import { IconTrash } from './QuirkIcons';

/**
 * Create a button that deletes an item
 * @param {T} params.item The item to delete
 * @param {MutationDefinition} params.mutation The useDelete*Mutation hook from RTK Query
 * @param {React.FC} params.confirmModal The modal that pops up to confirm deletion
 * @param {Function} params.action An optional action to perform after deletion
 * @param {string} params.label The label for the button
 * @param {string} params.landingPage The page to redirect to after deletion
 * @returns {React.FC} A button that deletes the item
 */
export function DeleteItem<T extends Document>({
	item,
	mutation,
	hideTooltip,
	dismissLabel,
	actionLabel,
	landingPage,
	modalContent,
	testId
}: {
	item: T;
	mutation: UseMutation<MutationDefinition<T['_id'], BaseQueryType, string, T, 'BaseApi'>>;
	hideTooltip?: () => void;
	actionLabel: string;
	dismissLabel: string;
	landingPage?: () => void;
	modalContent: ReactElement;
	testId?: string;
}) {
	const [deleteItem] = mutation();

	const onDelete = async () => {
		hideTooltip && hideTooltip();
		dismiss();

		await deleteItem(item._id);
		landingPage && landingPage();
	};

	const removeTooltip = async (dismiss: () => void) => {
		await dismiss();
		hideTooltip && hideTooltip();
	};
	const [present, dismiss] = useIonModal(Confirmation, {
		onDismiss: () => removeTooltip(dismiss),
		action: onDelete,
		actionLabel,
		dismissLabel,
		content: <>{modalContent}</>
	});

	return (
		<IconButton
			label={actionLabel || 'Delete'}
			icon={<IconTrash />}
			onClick={() => present({ cssClass: 'deleteModal' })}
			testId={testId}
		/>
	);
}
